import { formatApiErrorDetail } from "./api";

export const EMPLOYEE_SERVICE_BASE = "/hr/payroll/employee-service";

export const EMPLOYEE_SERVICE_PATHS = Object.freeze({
  collection: EMPLOYEE_SERVICE_BASE,
  detail: (id) => `${EMPLOYEE_SERVICE_BASE}/${encodeURIComponent(id)}`,
  submit: (id) => `${EMPLOYEE_SERVICE_BASE}/${encodeURIComponent(id)}/submit`,
  actions: (id) => `${EMPLOYEE_SERVICE_BASE}/${encodeURIComponent(id)}/actions`,
  members: (id) => `${EMPLOYEE_SERVICE_BASE}/${encodeURIComponent(id)}/members`,
  member: (caseId, memberId) =>
    `${EMPLOYEE_SERVICE_BASE}/${encodeURIComponent(caseId)}/members/${encodeURIComponent(memberId)}`,
  memberCandidates: (id) =>
    `${EMPLOYEE_SERVICE_BASE}/${encodeURIComponent(id)}/member-candidates`,
  evidence: (id) => `${EMPLOYEE_SERVICE_BASE}/${encodeURIComponent(id)}/evidence`,
  evidenceDetail: (id) =>
    `${EMPLOYEE_SERVICE_BASE}/evidence/${encodeURIComponent(id)}`,
  me: `${EMPLOYEE_SERVICE_BASE}/me`,
  summary: `${EMPLOYEE_SERVICE_BASE}/summary`,
  catalogue: `${EMPLOYEE_SERVICE_BASE}/catalogue`,
  policies: `${EMPLOYEE_SERVICE_BASE}/policies`,
  pulseAggregate: (id) => `${EMPLOYEE_SERVICE_BASE}/surveys/${encodeURIComponent(id)}/aggregate`,
});

export const EMPLOYEE_SERVICE_TABS = Object.freeze([
  { id: "overview", label: "Overview", kind: null },
  { id: "requests", label: "Service requests", kind: "service_request" },
  { id: "relations", label: "Employee relations", kind: "relations_case" },
  { id: "catalogue", label: "Service catalogue", kind: "service_definition" },
  { id: "pulse", label: "Pulse surveys", kind: "pulse_survey" },
]);

export const EMPLOYEE_SERVICE_EMPLOYMENT_TYPES = Object.freeze([
  "full_time",
  "part_time",
  "contract",
  "temporary",
  "intern",
]);

export const EMPLOYEE_RELATIONS_CASE_ROLES = Object.freeze([
  "owner",
  "investigator",
  "reviewer",
  "safeguarding_lead",
  "legal",
]);

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};
const asArray = (value) => (Array.isArray(value) ? value : []);
const asInteger = (value, fallback, minimum = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= minimum
    ? Math.floor(parsed)
    : fallback;
};

export function parseEmployeeServiceAudienceList(value, { lowercase = false } = {}) {
  const source = Array.isArray(value) ? value : String(value || "").split(/[\n,]/);
  const normalized = source
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .map((item) => (lowercase ? item.toLowerCase() : item));
  return [...new Set(normalized)];
}

export function employeeServiceAudiencePayload(values, prefix) {
  const key = String(prefix || "");
  if (!["eligible", "audience"].includes(key)) {
    throw new Error("Employee Service audience prefix must be eligible or audience.");
  }
  return {
    [`${key}_branch_ids`]: parseEmployeeServiceAudienceList(
      values?.[`${key}_branch_ids`],
    ),
    [`${key}_role_types`]: parseEmployeeServiceAudienceList(
      values?.[`${key}_role_types`],
      { lowercase: true },
    ),
    [`${key}_employment_types`]: parseEmployeeServiceAudienceList(
      values?.[`${key}_employment_types`],
      { lowercase: true },
    ),
  };
}

export function employeeServiceAudienceSummary(record, prefix) {
  const key = String(prefix || "");
  const branches = asArray(record?.[`${key}_branch_ids`]);
  const roles = asArray(record?.[`${key}_role_types`]);
  const employmentTypes = asArray(record?.[`${key}_employment_types`]);
  return {
    unrestricted: branches.length === 0 && roles.length === 0 && employmentTypes.length === 0,
    branches,
    roles,
    employmentTypes,
  };
}

export function normalizeEmployeeServiceList(body) {
  const source = asObject(body?.data && !Array.isArray(body.data) ? body.data : body);
  const items = asArray(source.items || source.records);
  const page = asInteger(source.page, 1, 1);
  const limit = asInteger(source.limit, 25, 1);
  const reportedTotal = asInteger(source.total, items.length, 0);
  const visibleCoverage = items.length ? (page - 1) * limit + items.length : 0;
  const total = Math.max(reportedTotal, visibleCoverage);
  const pages = Math.max(1, Math.ceil(total / limit));
  const first = items.length ? (page - 1) * limit + 1 : 0;
  const last = items.length ? first + items.length - 1 : 0;
  return {
    items,
    page,
    limit,
    total,
    pages,
    returned: items.length,
    first,
    last,
    hasMore:
      typeof source.has_more === "boolean"
        ? source.has_more
        : page * limit < total,
  };
}

export function normalizeEmployeeServiceDetail(body) {
  const source = asObject(body?.data && !Array.isArray(body.data) ? body.data : body);
  return {
    item: asObject(source.item || source.record || source),
    activities: asArray(source.activities),
    evidence: asArray(source.evidence),
    members: asArray(source.members),
    aggregate: asObject(source.aggregate),
    accessBasis: source.access_basis || null,
    relatedCases: asArray(source.related_cases),
    warnings: asArray(source.warnings),
    availableActions: asArray(source.available_actions || source.availableActions),
  };
}

export function normalizeEmployeeServiceSelf(body) {
  const source = asObject(body?.data && !Array.isArray(body.data) ? body.data : body);
  return {
    employee: asObject(source.employee),
    requests: asArray(source.requests),
    relationsCases: asArray(source.relations_cases),
    openSurveys: asArray(source.open_surveys),
    recognitionFeed: asArray(source.recognition_feed),
  };
}

export function employeeServicePermissionGates(can) {
  const allowed = (permission) => typeof can === "function" && Boolean(can(permission));
  const canRead = allowed("employee_service:read");
  const canCreate = allowed("employee_service:create");
  const canUpdate = allowed("employee_service:update");
  const canApprove = allowed("employee_service:approve");
  const canReadAll = allowed("employee_service:read_all");
  const canConfigure = allowed("employee_service:configure");
  const canReadRelations = allowed("employee_relations:read");
  const canOperateRequests =
    canRead && canUpdate && (canApprove || canReadAll);
  return {
    canRead,
    canAccessWorkspace: canOperateRequests || canConfigure || canReadRelations,
    canCreate,
    canUpdate,
    canApprove,
    canReadAll,
    canOperateRequests,
    canConfigure,
    canReadSensitive: allowed("employee_service_sensitive:read"),
    canUpdateSensitive: allowed("employee_service_sensitive:update"),
    canApproveSensitive: allowed("employee_service_sensitive:approve"),
    canReadRelations,
    canUpdateRelations: allowed("employee_relations:update"),
    canApproveRelations: allowed("employee_relations:approve"),
    canConfigureRelations: allowed("employee_relations:configure"),
    canHandleDiscipline: allowed("employee_relations:discipline"),
    canHandleSafeguarding: allowed("employee_relations:safeguarding"),
  };
}

export function hasEmployeeServiceWorkspaceAccess(can) {
  return employeeServicePermissionGates(can).canAccessWorkspace;
}

export function employeeServiceStatusMeta(value) {
  const status = String(value || "draft").toLowerCase();
  if (["resolved", "closed", "published", "open", "upheld", "available"].includes(status)) {
    return { label: humanizeEmployeeServiceValue(status), tone: "green" };
  }
  if (
    [
      "cancelled",
      "archived",
      "overturned",
      "critical",
      "quarantined",
      "unavailable",
      "overdue",
      "breached",
    ].includes(status)
  ) {
    return { label: humanizeEmployeeServiceValue(status), tone: "red" };
  }
  if (
    [
      "submitted",
      "assigned",
      "triage",
      "investigating",
      "waiting_employee",
      "appealed",
      "scan_pending",
      "near_breach",
    ].includes(status)
  ) {
    return { label: humanizeEmployeeServiceValue(status), tone: "amber" };
  }
  if (
    [
      "in_progress",
      "awaiting_response",
      "decision_pending",
      "reopened",
      "within_sla",
    ].includes(status)
  ) {
    return { label: humanizeEmployeeServiceValue(status), tone: "blue" };
  }
  return { label: humanizeEmployeeServiceValue(status), tone: "gray" };
}

export function humanizeEmployeeServiceValue(value) {
  return (
    String(value || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (character) => character.toUpperCase()) || "Not set"
  );
}

export function employeeServiceRecordName(record) {
  return (
    record?.name ||
    record?.title ||
    record?.subject ||
    record?.request_number ||
    record?.case_number ||
    record?.code ||
    "Employee service record"
  );
}

export function employeeServiceOperationKey(operation = "mutation") {
  const suffix =
    (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `employee-service-${String(operation)
    .replace(/[^a-z0-9-]+/gi, "-")
    .toLowerCase()}-${suffix}`;
}

export function parseEmployeeServiceError(
  error,
  fallback = "The employee service request could not be completed.",
) {
  const response = error?.response;
  const detail = response?.data?.detail ?? response?.data ?? error?.message;
  const objectDetail = asObject(detail);
  const status = Number(response?.status || error?.status || 0) || null;
  return {
    status,
    code: objectDetail.code || response?.data?.code || null,
    message: formatApiErrorDetail(detail) || fallback,
    conflict: status === 409 || status === 412,
    currentVersion: objectDetail.current_version ?? null,
    correlationId:
      response?.headers?.["x-correlation-id"] || objectDetail.correlation_id || null,
  };
}

export function canRunEmployeeServiceAction(action, gates, kind, accessBasis = null) {
  const name = String(action || "").toLowerCase();
  if (kind === "relations_case") {
    return ["record_outcome", "decide_appeal"].includes(name)
      ? gates.canApproveRelations
      : gates.canUpdateRelations;
  }
  if (kind === "service_definition" || kind === "pulse_survey") return gates.canConfigure;
  if (kind === "service_request" && name === "escalate") {
    return Boolean(
      accessBasis !== "owner" && gates.canOperateRequests && gates.canApprove,
    );
  }
  if (kind === "service_request" && accessBasis === "owner") {
    return ["submit", "cancel", "resume", "reopen"].includes(name);
  }
  if (kind === "service_request" && !gates.canOperateRequests) return false;
  if (name === "resolve") return gates.canApprove;
  return gates.canUpdate;
}
